import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';
import { servicesData } from '../data/services';
import { promoCategories } from '../data/promoProducts'; // Importing the new categories

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Dropdown States
  const [desktopServicesOpen, setDesktopServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  const [desktopPromoOpen, setDesktopPromoOpen] = useState(false);
  const [mobilePromoOpen, setMobilePromoOpen] = useState(false);

  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close all menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setDesktopServicesOpen(false);
    setMobileServicesOpen(false);
    setDesktopPromoOpen(false);
    setMobilePromoOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Promotional Products', path: '/promotional-products' }, // New Link
    // { name: 'Portfolio', path: '/portfolio' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <>
      {/* Floating Glassmorphism Container */}
      <nav className={`fixed top-0 w-full z-50 pt-4 px-4 md:px-8 transition-all duration-500 ${isScrolled ? 'pt-2' : 'pt-6'}`}>
        <div className={`mx-auto max-w-7xl bg-white/85 backdrop-blur-xl border border-white/40 rounded-2xl px-6 py-3 flex justify-between items-center transition-shadow duration-500 ${isScrolled ? 'shadow-2xl shadow-brand-dark/10' : 'shadow-lg shadow-brand-dark/5'}`}>

          {/* Logo */}
          <Link to="/" className="flex items-center z-50 hover:scale-105 transition-transform duration-300">
            <img
              src="/best-sign-company-in-usa.png"
              alt="A2Z Signs - Best Sign Company in USA"
              className="h-10 md:h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Links */}
          <div className="hidden lg:flex gap-6 xl:gap-8 font-bold text-sm uppercase tracking-wider items-center h-full">
            {navLinks.map((item) => {

              // 1. SERVICES MEGA MENU
              if (item.name === 'Services') {
                return (
                  <div
                    key={item.name}
                    className="relative group py-2"
                    onMouseEnter={() => setDesktopServicesOpen(true)}
                    onMouseLeave={() => setDesktopServicesOpen(false)}
                  >
                    <Link
                      to={item.path}
                      className={`flex items-center gap-1 transition-colors relative ${location.pathname.includes('/services') ? 'text-brand-cyan' : 'text-brand-dark hover:text-brand-cyan'}`}
                    >
                      {item.name}
                      <ChevronDown size={16} className={`transition-transform duration-300 ${desktopServicesOpen ? 'rotate-180 text-brand-cyan' : ''}`} />
                      <span className={`absolute -bottom-1 left-0 h-0.5 bg-brand-cyan transition-all duration-300 ${location.pathname === item.path ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
                    </Link>

                    <AnimatePresence>
                      {desktopServicesOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 15 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 15 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full right-[-100px] w-[800px] xl:w-[1000px] bg-white shadow-2xl rounded-2xl border border-gray-100 overflow-hidden cursor-default before:absolute before:-top-6 before:left-0 before:w-full before:h-6"
                        >
                          <div className="p-8">
                            <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
                              <h3 className="text-xl font-heading font-bold text-brand-dark normal-case">All Custom Signage Solutions</h3>
                              <Link to="/services" className="text-sm font-bold text-brand-cyan hover:text-brand-blue flex items-center gap-1 normal-case">
                                View Main Catalog <ArrowRight size={16} />
                              </Link>
                            </div>

                            <div className="grid grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-4">
                              {servicesData.map((service) => (
                                <Link
                                  key={service.id}
                                  to={`/services/${service.id}`}
                                  className="text-gray-600 hover:text-brand-cyan text-sm font-semibold transition-colors truncate block normal-case"
                                  title={service.title}
                                >
                                  {service.title}
                                </Link>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }

              // 2. PROMOTIONAL PRODUCTS COMPACT DROPDOWN
              if (item.name === 'Promotional Products') {
                return (
                  <div
                    key={item.name}
                    className="relative group py-2"
                    onMouseEnter={() => setDesktopPromoOpen(true)}
                    onMouseLeave={() => setDesktopPromoOpen(false)}
                  >
                    <Link
                      to={item.path}
                      className={`flex items-center gap-1 transition-colors relative ${location.pathname.includes('/promotional-products') ? 'text-brand-cyan' : 'text-brand-dark hover:text-brand-cyan'}`}
                    >
                      Promo Products
                      <ChevronDown size={16} className={`transition-transform duration-300 ${desktopPromoOpen ? 'rotate-180 text-brand-cyan' : ''}`} />
                      <span className={`absolute -bottom-1 left-0 h-0.5 bg-brand-cyan transition-all duration-300 ${location.pathname === item.path ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
                    </Link>

                    <AnimatePresence>
                      {desktopPromoOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 15 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 15 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-1/2 transform -translate-x-1/2 w-[260px] bg-white shadow-2xl rounded-2xl border border-gray-100 overflow-hidden cursor-default before:absolute before:-top-6 before:left-0 before:w-full before:h-6"
                        >
                          <div className="p-6">
                            <h3 className="text-sm font-heading font-bold text-gray-400 uppercase tracking-widest mb-4 pb-3 border-b border-gray-100">Categories</h3>
                            <div className="flex flex-col gap-4">
                              {promoCategories.map((category) => (
                                <Link
                                  key={category.slug}
                                  to={`/promotional-products/${category.slug}`}
                                  className="text-gray-700 hover:text-brand-cyan text-sm font-bold transition-colors block normal-case"
                                >
                                  {category.name}
                                </Link>
                              ))}
                            </div>
                            <div className="mt-5 pt-4 border-t border-gray-100 text-center">
                              <Link to="/promotional-products" className="text-xs font-black uppercase tracking-widest text-brand-cyan hover:text-brand-dark transition-colors flex items-center justify-center gap-1">
                                View All <ArrowRight size={14} />
                              </Link>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }

              // Standard Links
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`transition-colors relative group py-2 ${location.pathname === item.path ? 'text-brand-cyan' : 'text-brand-dark hover:text-brand-cyan'}`}
                >
                  {item.name}
                  <span className={`absolute -bottom-1 left-0 h-0.5 bg-brand-cyan transition-all duration-300 ${location.pathname === item.path ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
                </Link>
              );
            })}
          </div>

          {/* Mobile Menu Toggle Button */}
          <button className="lg:hidden text-brand-dark hover:text-brand-cyan relative z-50 transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={32} /> : <Menu size={32} />}
          </button>
        </div>
      </nav>

      {/* Mobile Full-Screen Dropdown Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: '100vh' }}
            exit={{ opacity: 0, height: 0 }}
            className="fixed top-0 left-0 w-full bg-white/95 backdrop-blur-2xl z-40 lg:hidden overflow-y-auto custom-scrollbar pt-32 pb-10 border-b border-gray-200 shadow-2xl"
          >
            <div className="flex flex-col items-center w-full gap-6">
              {navLinks.map((item) => {

                // Mobile Services Accordion
                if (item.name === 'Services') {
                  return (
                    <div key={item.name} className="w-full px-8 flex flex-col items-center">
                      <button
                        onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                        className={`text-2xl font-heading font-bold tracking-wide flex items-center gap-2 transition-colors ${mobileServicesOpen || location.pathname.includes('/services') ? 'text-brand-cyan' : 'text-brand-dark'}`}
                      >
                        {item.name} <ChevronDown size={24} className={`transition-transform duration-300 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                      </button>

                      <AnimatePresence>
                        {mobileServicesOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="w-full overflow-hidden flex flex-col items-center mt-6"
                          >
                            <Link to="/services" className="px-6 py-2 bg-brand-cyan text-white font-bold rounded-full mb-6 text-sm">
                              View Main Catalog
                            </Link>
                            <div className="w-full max-h-[40vh] overflow-y-auto custom-scrollbar bg-gray-50 rounded-2xl p-4 flex flex-col gap-4 text-center border border-gray-200">
                              {servicesData.map((service) => (
                                <Link key={service.id} to={`/services/${service.id}`} className="text-gray-600 hover:text-brand-cyan font-medium text-sm transition-colors border-b border-gray-200 pb-3 last:border-0 last:pb-0">
                                  {service.title}
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                // Mobile Promo Products Accordion
                if (item.name === 'Promotional Products') {
                  return (
                    <div key={item.name} className="w-full px-8 flex flex-col items-center">
                      <button
                        onClick={() => setMobilePromoOpen(!mobilePromoOpen)}
                        className={`text-2xl font-heading font-bold tracking-wide flex items-center gap-2 transition-colors ${mobilePromoOpen || location.pathname.includes('/promotional-products') ? 'text-brand-cyan' : 'text-brand-dark'}`}
                      >
                        Promo Products <ChevronDown size={24} className={`transition-transform duration-300 ${mobilePromoOpen ? 'rotate-180' : ''}`} />
                      </button>

                      <AnimatePresence>
                        {mobilePromoOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="w-full overflow-hidden flex flex-col items-center mt-6"
                          >
                            <div className="w-full bg-gray-50 rounded-2xl p-4 flex flex-col gap-4 text-center border border-gray-200">
                              {promoCategories.map((category) => (
                                <Link
                                  key={category.slug}
                                  to={`/promotional-products/${category.slug}`}
                                  onClick={() => setMobileMenuOpen(false)}
                                  className="text-gray-600 hover:text-brand-cyan font-bold text-sm transition-colors border-b border-gray-200 pb-3 last:border-0 last:pb-0"
                                >
                                  {category.name}
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`text-2xl font-heading font-bold tracking-wide ${location.pathname === item.path ? 'text-brand-cyan' : 'text-brand-dark'}`}
                  >
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style dangerouslySetInnerHTML={{
        __html: `
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(0, 198, 255, 0.5); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(0, 198, 255, 1); }
      `}} />
    </>
  );
};

export default Navbar;